import React from "react";
import { Box } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { tokens } from "../../theme";

export interface ArtThumbnailProps {
  /** Stable id (track id) — drives the deterministic gradient. */
  id: string;
  size?: number;
  showPlay?: boolean;
  onPlay?: () => void;
}

/** Deterministic hue [0,360) from an id — stable across renders/reloads. */
export const hueFromId = (id: string): number => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
};

/**
 * Track artwork placeholder. Tracks have no artwork URL in the proto, so we
 * derive a stable gradient per track id (no Math.random — never reshuffles).
 */
export const ArtThumbnail: React.FC<ArtThumbnailProps> = ({
  id,
  size = 56,
  showPlay = false,
  onPlay,
}) => {
  const h = hueFromId(id);
  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: `${tokens.radius.sm}px`,
        background: `linear-gradient(150deg, hsl(${h} 55% 32%), hsl(${
          (h + 40) % 360
        } 50% 18%))`,
        boxShadow: tokens.shadow.edgeHi,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
      }}
    >
      {showPlay && (
        <Box
          component="button"
          type="button"
          aria-label="Play"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onPlay?.();
          }}
          sx={{
            width: Math.round(size * 0.46),
            height: Math.round(size * 0.46),
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            color: tokens.color.accentInk,
            background: tokens.gradient.accent,
            boxShadow: `${tokens.shadow.glow}, inset 0 1px 0 rgba(255,255,255,.3)`,
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: Math.round(size * 0.3) }} />
        </Box>
      )}
    </Box>
  );
};

export default ArtThumbnail;
